import { useState } from 'react';
import { Search, Eye, Heart, MessageCircle, Share2, RefreshCw } from 'lucide-react';
import { useTiktokVideos, useTiktokVideoSync, useTiktokVideoToggleMonitor } from '@/api/useTiktok';
import { useTiktokStore } from '@/stores/useTiktokStore';
import type { TiktokVideo } from '@/types/outreach';
import styles from './TiktokVideoList.module.css';

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

interface VideoCardProps {
  video: TiktokVideo;
  isSelected: boolean;
  onSelect: () => void;
}

function VideoCard({ video, isSelected, onSelect }: VideoCardProps) {
  const toggleMonitor = useTiktokVideoToggleMonitor();
  const title = video.title ?? video.description ?? 'Untitled video';

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleMonitor.mutate({ videoId: video.id, isMonitored: !video.isMonitored });
  };

  return (
    <div className={`${styles.card} ${isSelected ? styles.cardSelected : ''}`} onClick={onSelect}>
      <div className={styles.cardInner}>
        {video.thumbnailUrl ? (
          <img src={video.thumbnailUrl} alt={title} className={styles.thumbnail} />
        ) : (
          <div className={styles.thumbnailPlaceholder}>
            <span className={styles.tiktokIcon}>TT</span>
          </div>
        )}
        <div className={styles.info}>
          <p className={styles.title} title={title}>
            {title}
          </p>
          <div className={styles.stats}>
            <span className={styles.stat}>
              <Eye className={styles.statIcon} />
              {formatCount(video.viewCount)}
            </span>
            <span className={styles.stat}>
              <Heart className={styles.statIcon} />
              {formatCount(video.likeCount)}
            </span>
            <span className={styles.stat}>
              <MessageCircle className={styles.statIcon} />
              {formatCount(video.commentCount)}
            </span>
            <span className={styles.stat}>
              <Share2 className={styles.statIcon} />
              {formatCount(video.shareCount)}
            </span>
          </div>
          <button
            onClick={handleToggle}
            disabled={toggleMonitor.isPending}
            className={`${styles.monitorBadge} ${video.isMonitored ? styles.monitorBadgeOn : styles.monitorBadgeOff}`}
          >
            {video.isMonitored ? 'Monitored' : 'Not Monitored'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function TiktokVideoList() {
  const [search, setSearch] = useState('');
  const { data, isLoading } = useTiktokVideos(search || undefined);
  const sync = useTiktokVideoSync();
  const { selectedVideoId, setSelectedVideoId } = useTiktokStore();

  const videos = data?.results ?? [];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.searchWrapper}>
          <Search className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search videos..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <button
          onClick={() => sync.mutate()}
          disabled={sync.isPending}
          className={styles.syncBtn}
          title="Sync videos from TikTok"
        >
          <RefreshCw className={`${styles.syncIcon} ${sync.isPending ? styles.spinning : ''}`} />
        </button>
      </div>

      <div className={styles.list}>
        {isLoading && <div className={styles.empty}>Loading videos...</div>}
        {!isLoading && videos.length === 0 && (
          <div className={styles.empty}>No videos found. Sync to fetch from TikTok.</div>
        )}
        {videos.map((video) => (
          <VideoCard
            key={video.id}
            video={video}
            isSelected={selectedVideoId === video.tiktokVideoId}
            onSelect={() => setSelectedVideoId(video.tiktokVideoId)}
          />
        ))}
      </div>
    </div>
  );
}
